/**
 * The one date component (spec docs/features/v1-completion/N_time_spec.md §6):
 * how a page shows a Speed's date and state. Headless — it chooses among the
 * backend's own sentences (Time's wording) by the state the backend sent, and
 * never composes a date: no page builds "Arrives …" itself.
 */

import type {
  CataloguePriceOption,
  CataloguePriceOptions,
} from "../data/supplier-pricing-types";
import type {
  ClockStartBasis,
  SpeedChoice,
  SpeedLadderRung,
  SpeedOffer,
} from "../data/speed-types";

/** What a Speed row says about its date: the arrival line and, when there is one, a note under it. */
export interface SpeedDate {
  /** The backend's arrival sentence: "Arrives Fri 23 Oct", "Usually arrives …". */
  arrival: string;
  /** "Check with our team" in place of a promise (d67). */
  isToBeConfirmed: boolean;
  /** Why a person confirms it, or the approve-by sentence a needed-by date asks for. */
  note: string | null;
}

export function speedDate(offer: SpeedOffer): SpeedDate {
  if (offer.isToBeConfirmed) {
    const onTheDay = offer.arrival.daysToSpare === 0;
    return {
      arrival: offer.arrival.sentence,
      isToBeConfirmed: true,
      note: onTheDay
        ? "Arrives on the day, with no days to spare. We confirm it before we start; the line will not release until then."
        : "A person confirms this date before it is promised; the line will not release until then.",
    };
  }
  return {
    arrival: offer.arrival.sentence,
    isToBeConfirmed: false,
    note: offer.arrival.approveBySentence,
  };
}

/** The one hint under the Speed heading: the default's reason, or how the dates were worked out. */
export function speedListHint(
  speeds: SpeedChoice,
  basis: ClockStartBasis | null,
): string {
  if (speeds.offers.length === 0) return speeds.defaultReason;
  // "Standard" is the default without anything to explain (d59).
  if (speeds.defaultReason !== "Standard") {
    // A reason that opens with a Speed's name keeps its capital.
    const opensWithSpeed = [...speeds.offers, ...speeds.unavailable].some((s) =>
      speeds.defaultReason.startsWith(s.label),
    );
    const reason = opensWithSpeed
      ? speeds.defaultReason
      : lowerFirst(speeds.defaultReason);
    return `Default: ${reason}.`;
  }
  return basis === "assumed_approve_by"
    ? "Arrival assumes the proof is approved by the approve-by date."
    : "Arrival assumes the proof is approved today.";
}

/** "+£14.10 over Economy": what the next faster Speed costs over the selected one. */
export function quickerDifference(
  speeds: SpeedChoice,
  formatPence: (pence: number) => string,
): string | null {
  const quicker = speeds.offers.find((o) => o.speedCode === speeds.quickerCode);
  const selected = speeds.offers.find(
    (o) => o.speedCode === speeds.selectedCode,
  );
  if (!quicker || !selected) return null;
  const difference = quicker.pricePence - selected.pricePence;
  return difference > 0
    ? `+${formatPence(difference)} over ${selected.label}`
    : `No more than ${selected.label}`;
}

/** The options a Speed's "Show" table lists: those whose own Speed it is, its pick first. */
export function optionsBehindSpeed(
  priceOptions: CataloguePriceOptions,
  offer: SpeedOffer,
): CataloguePriceOption[] {
  const own = priceOptions.options.filter(
    (o) => o.speedCode === offer.speedCode,
  );
  const pick = priceOptions.options.find((o) => o.key === offer.optionKey);
  return pick && !own.includes(pick) ? [pick, ...own] : own;
}

/** The options no Speed offers: slower than every Speed, or with no date and why. */
export function optionsInNoSpeed(
  priceOptions: CataloguePriceOptions,
): { option: CataloguePriceOption; why: string }[] {
  return priceOptions.options
    .filter((o) => o.speedCode == null)
    .map((o) => ({
      option: o,
      why: o.arrival
        ? `${lowerFirst(o.arrival.sentence)}, slower than every Speed`
        : (o.noArrivalReason ?? "no date can be given"),
    }));
}

/** "Every Speed", "Standard only", "Economy and Standard". */
export function describeSpeedNarrowing(
  allowedSpeeds: string[] | null | undefined,
  ladder: ReadonlyArray<SpeedLadderRung>,
): string {
  if (allowedSpeeds == null) return "Every Speed";
  const labels = ladder
    .filter((s) => allowedSpeeds.includes(s.speedCode))
    .map((s) => s.label);
  if (labels.length === 1) return `${labels[0]} only`;
  return labels.join(" and ");
}

function lowerFirst(text: string): string {
  return text.length > 0 ? text[0].toLowerCase() + text.slice(1) : text;
}
